import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LogOut, Trash2, UserPlus } from 'lucide-react';
import { AREA_ROLE_LABELS, type AreaMember, type AreaRole, type NavArea } from '@kaba/shared';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { refreshStructure, useNav } from '../../lib/structure';
import { useTeam } from '../../lib/teams';
import { toast } from '../../lib/toast';
import { Avatar, Button, IconButton, Sheet } from '../ui/ui';

const field = 'min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-base sm:min-h-10 sm:text-sm';

/** Neuer Bereich bzw. Bereich umbenennen */
export function AreaFormDialog({
  area,
  open,
  onClose,
  onCreated,
}: {
  area?: NavArea | null;
  open: boolean;
  onClose: () => void;
  onCreated?: (id: string) => void;
}) {
  const { data: nav } = useNav(open);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [teamId, setTeamId] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setName(area?.name ?? '');
    setDescription(area?.description ?? '');
    setTeamId(area?.teamId ?? nav?.teams[0]?.id ?? '');
  }, [open, area, nav?.teams]);
  const teams = nav?.teams ?? [];
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title={area ? 'Bereich bearbeiten' : 'Neuer Bereich'} side="center">
      <form
        className="flex flex-col gap-4 p-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            if (area) await api.patch(`/api/areas/${area.id}`, { name, description: description || null });
            else {
              const r = await api.post<{ id: string }>(`/api/teams/${teamId}/areas`, { name, description: description || null });
              await refreshStructure();
              onCreated?.(r.id);
            }
            void refreshStructure();
            onClose();
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Name
          <input
            className={field}
            required
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder="z. B. Kundenprojekte"
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Beschreibung (optional)
          <input className={field} maxLength={300} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        {!area && teams.length > 1 && (
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Team
            <select className={field} value={teamId} onChange={(e) => setTeamId(e.target.value)}>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {!area && (
          <p className="text-xs text-[var(--ink-soft)]">
            Du wirst Verwalter des Bereichs. Ein Bereich ordnet Boards – er gibt niemandem Zugriff darauf. Freigaben regelst du weiter im jeweiligen Board.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={busy || !name.trim() || (!area && !teamId)}>
            {area ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

/** Neuer Ordner bzw. Ordner umbenennen (eine Ebene im Bereich) */
export function FolderFormDialog({
  areaId,
  folder,
  open,
  onClose,
  onCreated,
}: {
  areaId: string;
  folder?: { id: string; name: string } | null;
  open: boolean;
  onClose: () => void;
  onCreated?: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setName(folder?.name ?? '');
  }, [open, folder]);
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title={folder ? 'Ordner umbenennen' : 'Neuer Ordner'} side="center">
      <form
        className="flex flex-col gap-4 p-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            if (folder) await api.patch(`/api/folders/${folder.id}`, { name });
            else {
              const r = await api.post<{ id: string }>(`/api/areas/${areaId}/folders`, { name });
              await refreshStructure();
              onCreated?.(r.id);
            }
            void refreshStructure();
            onClose();
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Name
          <input className={field} required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="z. B. Kunde Müller" />
        </label>
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={busy || !name.trim()}>
            {folder ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

/** Mitglieder eines Bereichs (R03): betrifft nur die Struktur, nie Boardinhalte */
export function AreaMembersDialog({ area, open, onClose, onLeft }: { area: NavArea; open: boolean; onClose: () => void; onLeft: () => void }) {
  const { data: me } = useMe();
  const key = ['area-members', area.id];
  const { data: members, refetch } = useQuery({ queryKey: key, queryFn: () => api.get<AreaMember[]>(`/api/areas/${area.id}/members`), enabled: open });
  const { data: team } = useTeam(open && area.canManage ? area.teamId : null);
  const [adding, setAdding] = useState('');
  const manage = area.canManage;
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    try {
      await fn();
      if (ok) toast.info(ok);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      void refetch();
      refreshStructure();
    }
  };
  const candidates = (team?.members ?? []).filter((m) => !members?.some((x) => x.id === m.id));
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title={`Mitglieder von „${area.name}“`} side="center">
      <div className="flex flex-col gap-4 p-4">
        <p className="text-sm text-[var(--ink-soft)]">
          Mitglieder sehen die Struktur dieses Bereichs und legen hier eigene Boards an. Das gibt keinen Zugriff auf Boards – den regeln die Freigaben im
          jeweiligen Board.
        </p>
        {manage && candidates.length > 0 && (
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!adding) return;
              const who = candidates.find((c) => c.id === adding);
              void run(
                () => api.put(`/api/areas/${area.id}/members/${adding}`, { role: 'member' }),
                `${who?.name ?? 'Die Person'} sieht jetzt die Struktur des Bereichs.`,
              );
              setAdding('');
            }}
          >
            <label className="min-w-0 flex-1 basis-full sm:basis-auto">
              <span className="sr-only">Teammitglied hinzufügen</span>
              <select className={field} value={adding} onChange={(e) => setAdding(e.target.value)}>
                <option value="">Teammitglied wählen …</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <Button type="submit" variant="primary" disabled={!adding}>
              <UserPlus size={16} aria-hidden /> Hinzufügen
            </Button>
          </form>
        )}
        <ul aria-label="Mitglieder des Bereichs" className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
          {members?.map((m) => (
            <li key={m.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
              <Avatar user={m} size={30} />
              <div className="min-w-[55%] flex-1 sm:min-w-0">
                <p className="truncate text-sm font-medium">
                  {m.name}
                  {m.id === me?.id && ' (du)'}
                </p>
                <p className="truncate text-xs text-[var(--ink-soft)]">{m.email}</p>
              </div>
              {manage ? (
                <select
                  aria-label={`Rolle von ${m.name} im Bereich`}
                  className={`${field} ml-10 w-auto flex-1 sm:ml-0 sm:flex-none`}
                  value={m.role}
                  onChange={(e) => void run(() => api.put(`/api/areas/${area.id}/members/${m.id}`, { role: e.target.value as AreaRole }))}
                >
                  <option value="member">{AREA_ROLE_LABELS.member}</option>
                  <option value="manager">{AREA_ROLE_LABELS.manager}</option>
                </select>
              ) : (
                <span className="text-xs text-[var(--ink-soft)]">{AREA_ROLE_LABELS[m.role]}</span>
              )}
              {m.id === me?.id ? (
                <IconButton
                  label="Bereich verlassen"
                  onClick={() => {
                    if (
                      !window.confirm(
                        `„${area.name}“ verlassen?\n\nDu siehst die Struktur des Bereichs dann nicht mehr. Deine Boardfreigaben bleiben bestehen.`,
                      )
                    )
                      return;
                    void run(async () => {
                      await api.del(`/api/areas/${area.id}/members/${m.id}`);
                      onLeft();
                    }, 'Du hast den Bereich verlassen. Deine Boards findest du weiter unter „Alle Boards“.');
                  }}
                >
                  <LogOut size={16} />
                </IconButton>
              ) : (
                manage && (
                  <IconButton
                    label={`${m.name} aus dem Bereich entfernen`}
                    onClick={() => {
                      if (!window.confirm(`${m.name} aus „${area.name}“ entfernen? Boardfreigaben bleiben bestehen.`)) return;
                      void run(() => api.del(`/api/areas/${area.id}/members/${m.id}`));
                    }}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                )
              )}
            </li>
          ))}
        </ul>
      </div>
    </Sheet>
  );
}
